import { useCallback, useEffect, useMemo, useState } from "react";
import {
  listarRoles,
  crearRol,
  actualizarRol,
  eliminarRol,
} from "../../../services/rolesService";
import { listarPermisos } from "../../../services/permisosService";

const initialFormState = {
  nombre: "",
  descripcion: "",
  tipo: "operativo",
  permisos: [],
};

const sanitize = (value = "") =>
  typeof value === "string" ? value.trim() : "";

const toForm = (rol = {}) => ({
  id: rol.id,
  nombre: sanitize(rol.nombre) || "",
  descripcion: sanitize(rol.descripcion) || "",
  tipo: rol.tipo || "operativo",
  permisos: Array.isArray(rol.permisos)
    ? rol.permisos
      .map((permiso) => (typeof permiso === "string" ? permiso : permiso.codigo))
      .filter(Boolean)
    : [],
});

const arraysMatch = (a = [], b = []) => {
  const left = Array.from(new Set(a.filter(Boolean)));
  const right = Array.from(new Set(b.filter(Boolean)));
  if (left.length !== right.length) return false;
  const set = new Set(left);
  return right.every((item) => set.has(item));
};

const normalizeRoles = (roles = []) =>
  Array.isArray(roles)
    ? roles.map((rol) => ({
      ...rol,
      permisos: Array.isArray(rol.permisos) ? rol.permisos : [],
    }))
    : [];

const validateForm = (form) => {
  if (!sanitize(form.nombre)) return "El nombre del rol es obligatorio.";
  if (!sanitize(form.descripcion)) return "La descripcion no puede estar vacia.";
  if (!form.tipo) return "Selecciona el tipo de rol.";
  return null;
};

const mapPermisosFromCodes = (codes = [], catalogo = []) => {
  const list = Array.isArray(catalogo) ? catalogo : [];
  return codes.map(
    (code) => list.find((permiso) => permiso.codigo === code) || {
      codigo: code,
      descripcion: code,
    }
  );
};

export default function useRolesLogic() {
  const [roles, setRoles] = useState([]);
  const [permisos, setPermisos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permisosLoading, setPermisosLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [permisosError, setPermisosError] = useState(null);
  const [alerta, setAlerta] = useState(null);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [modo, setModo] = useState("");
  const [form, setForm] = useState({ ...initialFormState });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const showAlert = useCallback(
    (type, message) => setAlerta({ type, message }),
    []
  );
  const dismissAlert = useCallback(() => setAlerta(null), []);

  const loadRoles = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listarRoles();
      setRoles(normalizeRoles(data));
      setListError(null);
    } catch (error) {
      console.error(error);
      setListError(error.message || "No se pudo cargar la lista de roles.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPermisos = useCallback(async () => {
    setPermisosLoading(true);
    try {
      const data = await listarPermisos();
      setPermisos(Array.isArray(data) ? data : []);
      setPermisosError(null);
    } catch (error) {
      console.error(error);
      setPermisosError(error.message || "No se pudieron cargar los permisos.");
    } finally {
      setPermisosLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoles();
    loadPermisos();
  }, [loadRoles, loadPermisos]);

  useEffect(() => {
    if (modo === "nuevo") return;
    if (!roles.length) {
      setSelectedRoleId(null);
      return;
    }
    if (!selectedRoleId || !roles.some((rol) => rol.id === selectedRoleId)) {
      setSelectedRoleId(roles[0].id);
      setModo("editar");
    }
  }, [roles, selectedRoleId, modo]);

  const selectedRole = useMemo(
    () => roles.find((rol) => rol.id === selectedRoleId) || null,
    [roles, selectedRoleId]
  );

  useEffect(() => {
    if (modo === "nuevo") return;
    if (selectedRole) {
      setForm(toForm(selectedRole));
    }
  }, [selectedRole, modo]);

  const groupedPermisos = useMemo(() => {
    const grouped = {};
    (permisos || []).forEach((permiso) => {
      const modulo = permiso.modulo || "Otros";
      const submodulo = permiso.submodulo || "General";
      if (!grouped[modulo]) grouped[modulo] = {};
      if (!grouped[modulo][submodulo]) grouped[modulo][submodulo] = [];
      grouped[modulo][submodulo].push(permiso);
    });
    return grouped;
  }, [permisos]);

  const baseForm = useMemo(() => {
    if (modo === "nuevo") return initialFormState;
    if (!selectedRole) return null;
    return toForm(selectedRole);
  }, [modo, selectedRole]);

  const hasChanges = useMemo(() => {
    if (!baseForm) return false;
    const nombreChanged = sanitize(form.nombre) !== sanitize(baseForm.nombre);
    const descripcionChanged =
      sanitize(form.descripcion) !== sanitize(baseForm.descripcion);
    const tipoChanged = (form.tipo || "operativo") !== (baseForm.tipo || "operativo");
    const permisosChanged = !arraysMatch(form.permisos || [], baseForm.permisos || []);
    return nombreChanged || descripcionChanged || tipoChanged || permisosChanged;
  }, [form, baseForm]);

  const selectRole = useCallback(
    (roleId) => {
      const found = roles.find((rol) => rol.id === roleId);
      if (!found) return;
      setSelectedRoleId(found.id);
      setModo("editar");
      setForm(toForm(found));
    },
    [roles]
  );

  const startNewRole = useCallback(() => {
    setModo("nuevo");
    setSelectedRoleId(null);
    setForm({ ...initialFormState });
  }, []);

  const resetForm = useCallback(() => {
    if (modo === "nuevo") {
      setForm({ ...initialFormState });
      return;
    }
    if (selectedRole) {
      setForm(toForm(selectedRole));
    }
  }, [modo, selectedRole]);

  const togglePermiso = useCallback((codigo) => {
    if (!codigo) return;
    setForm((prev) => {
      const current = Array.isArray(prev.permisos) ? prev.permisos : [];
      const has = current.includes(codigo);
      const updated = has
        ? current.filter((item) => item !== codigo)
        : [...current, codigo];
      return { ...prev, permisos: updated };
    });
  }, []);

  const toggleGroup = useCallback((codes = [], enable) => {
    const validCodes = Array.isArray(codes) ? codes.filter(Boolean) : [];
    setForm((prev) => {
      const set = new Set(Array.isArray(prev.permisos) ? prev.permisos : []);
      if (enable) {
        validCodes.forEach((code) => set.add(code));
      } else {
        validCodes.forEach((code) => set.delete(code));
      }
      return { ...prev, permisos: Array.from(set) };
    });
  }, []);

  const buildPayload = useCallback(
    () => ({
      nombre: sanitize(form.nombre),
      descripcion: sanitize(form.descripcion),
      tipo: form.tipo || "operativo",
      permisos: Array.isArray(form.permisos)
        ? Array.from(new Set(form.permisos.filter(Boolean)))
        : [],
    }),
    [form]
  );

  const handleSubmit = useCallback(
    async (event) => {
      event?.preventDefault?.();
      const payload = buildPayload();
      const validation = validateForm(payload);
      if (validation) {
        showAlert("warning", validation);
        return;
      }

      setSaving(true);
      try {
        if (modo === "nuevo") {
          const response = await crearRol(payload);
          const id = response?.id ?? Date.now();
          const nuevoRol = {
            id,
            nombre: payload.nombre,
            descripcion: payload.descripcion,
            tipo: payload.tipo,
            permisos: mapPermisosFromCodes(payload.permisos, permisos),
          };
          setRoles((prev) => [nuevoRol, ...prev]);
          setSelectedRoleId(id);
          setModo("editar");
          showAlert("success", "Rol creado correctamente.");
        } else if (selectedRole) {
          await actualizarRol(selectedRole.id, payload);
          const actualizado = {
            ...selectedRole,
            ...payload,
            permisos: mapPermisosFromCodes(payload.permisos, permisos),
          };
          setRoles((prev) =>
            prev.map((rol) => (rol.id === actualizado.id ? actualizado : rol))
          );
          showAlert("success", "Cambios guardados correctamente.");
        }
      } catch (error) {
        console.error(error);
        showAlert("danger", error.message || "No se pudo guardar el rol.");
      } finally {
        setSaving(false);
      }
    },
    [buildPayload, modo, permisos, selectedRole, showAlert]
  );

  const handleDelete = useCallback(async () => {
    if (!selectedRole) return;
    setDeleting(true);
    try {
      await eliminarRol(selectedRole.id);
      setRoles((prev) => prev.filter((rol) => rol.id !== selectedRole.id));
      showAlert("success", "Rol eliminado correctamente.");
      if (roles.length <= 1) {
        setModo("nuevo");
        setSelectedRoleId(null);
        setForm({ ...initialFormState });
      }
    } catch (error) {
      console.error(error);
      showAlert("danger", error.message || "No se pudo eliminar el rol.");
    } finally {
      setDeleting(false);
    }
  }, [roles.length, selectedRole, showAlert]);

  return {
    data: {
      roles,
      permisos,
      groupedPermisos,
      selectedRole,
    },
    state: {
      loading,
      permisosLoading,
      listError,
      permisosError,
      saving,
      deleting,
      isNuevo: modo === "nuevo",
      hasChanges,
    },
    form: {
      values: form,
      setValues: setForm,
    },
    alerts: {
      alerta,
      dismissAlert,
    },
    selectors: {
    },
    actions: {
      selectRole,
      startNewRole,
      resetForm,
      togglePermiso,
      toggleGroup,
      handleSubmit,
      handleDelete,
      reloadRoles: loadRoles,
      reloadPermisos: loadPermisos,
    },
  };
}

export { initialFormState };
